import { Popover } from '@mantine/core'
import { DatePicker, DatesRangeValue } from '@mantine/dates'
import dayJS from 'dayjs'
import utc from 'dayjs/plugin/utc'
import React, { useState } from 'react'
import isEqual from 'react-fast-compare'
import styled from 'styled-components'
import { Button } from '../button'
import { DateRangeProps } from './date-range.types'

dayJS.extend(utc)

const StyledContainer = styled.div`
  ${({ theme }) => `
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: ${theme.sizes[4]};

    [data-weekend] {
      color: ${theme.colors.primary400};
    }

    [data-first-in-range] {
      border-top-left-radius: ${theme.sizes[4]};
      border-bottom-left-radius: ${theme.sizes[4]};
    }

    [data-in-range] {
      background-color: ${theme.colors.primary100};
      color: ${theme.colors.black};
    }

    [data-last-in-range] {
      border-top-right-radius: ${theme.sizes[4]};
      border-bottom-right-radius: ${theme.sizes[4]};
    }

    [data-selected] {
      background-color: ${theme.colors.primary400};
      color: ${theme.colors.white};
    }
  `}
`

const StyledButtonsContainer = styled.div`
  ${({ theme }) => `
    display: flex;
    align-items: flex-end;
    gap: ${theme.sizes[4]};
  `}
`

export const DateRange = React.memo(
  ({
    open,
    setOpen,
    queryParams,
    setQueryParams,
    children,
    applyButtonText = 'Apply',
    maxLevel = 'year',
    numberOfColumns = 2,
    withinPortal = true,
    clearButtonText,
    dropdownClassName,
    position = 'bottom-end',
    zIndex,
    ...restProps
  }: DateRangeProps) => {
    const qpStartDate = queryParams?.startDate
      ? dayJS(queryParams?.startDate).utc().toDate()
      : null
    const qpEndDate = queryParams?.endDate
      ? dayJS(queryParams?.endDate).endOf('day').toDate()
      : null
    const qpValue: DatesRangeValue = [qpStartDate, qpEndDate]

    const [value, setValue] = useState<[Date | null, Date | null]>(qpValue)
    const [displayedDate, setDisplayedDate] = useState<Date>(
      () => qpStartDate ?? new Date()
    )

    const handleDateChange = () => {
      const [startDate, endDate] = value
      setOpen(false)

      if (startDate && endDate) {
        setQueryParams?.({
          startDate: dayJS(startDate)
            .startOf('day')
            .format('YYYY-MM-DDTHH:mm:ssZ'),
          endDate: dayJS(endDate).endOf('day').format('YYYY-MM-DDTHH:mm:ssZ'),
        })
      }
    }

    const handleClearDate = () => {
      setValue([null, null])
      setQueryParams?.({
        startDate: undefined,
        endDate: undefined,
      })
      setOpen(false)
    }

    const handleClose = () => {
      setOpen(false)
    }

    const handleOpen = () => {
      setValue(qpValue)
      setDisplayedDate(qpStartDate ?? new Date())
      setOpen(true)
    }

    return (
      <Popover
        opened={open}
        position={position}
        zIndex={zIndex}
        shadow="md"
        onDismiss={handleClose}
        onClose={handleClose}
        onOpen={handleOpen}
        withinPortal={withinPortal}
        classNames={{
          dropdown: dropdownClassName,
        }}
      >
        <Popover.Target>{children}</Popover.Target>
        <Popover.Dropdown>
          <StyledContainer>
            <DatePicker
              type="range"
              value={value}
              onChange={setValue}
              date={displayedDate}
              onDateChange={setDisplayedDate}
              allowSingleDateInRange
              maxLevel={maxLevel}
              numberOfColumns={numberOfColumns}
              {...restProps}
            />
            <StyledButtonsContainer>
              {clearButtonText && (
                <Button
                  type="submit"
                  variant="tertiary"
                  data-testid="clear-date"
                  onClick={handleClearDate}
                  disabled={value.some((v) => !v)}
                >
                  {clearButtonText}
                </Button>
              )}
              <Button
                type="submit"
                data-testid="apply-date"
                onClick={handleDateChange}
                disabled={value.some((v) => !v)}
              >
                {applyButtonText}
              </Button>
            </StyledButtonsContainer>
          </StyledContainer>
        </Popover.Dropdown>
      </Popover>
    )
  },
  isEqual
)
